import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import { BOOKMAKER_ALIASES } from '../utils/constants';

// Create reverse alias map (caesars -> williamhill_us)
const REVERSE_ALIASES = Object.entries(BOOKMAKER_ALIASES).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

const NCAABGameModal = ({
  show,
  onHide,
  game,
  gameId,
  getTeamLogoUrl,
  getBookLogoUrl,
  bookmakers = []
}) => {
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSpreads, setShowSpreads] = useState(true);
  const [showTotals, setShowTotals] = useState(false);
  const [showEdgesOnly, setShowEdgesOnly] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tooltipData, setTooltipData] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch game data when modal opens
  useEffect(() => {
    if (show && gameId) {
      fetchGameData();
    } else if (!show) {
      setMarketData(null);
      setError(null);
    }
  }, [show, gameId]);

  const fetchGameData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/games/ncaab/${gameId}/odds/h2h?all=true`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setMarketData(data);
    } catch (err) {
      console.error('Error fetching game data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Close tooltip when clicking outside (mobile only)
  useEffect(() => {
    if (isMobile) {
      const handleClickOutside = () => setTooltipData(null);
      if (tooltipData) {
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
      }
    }
  }, [tooltipData, isMobile]);

  // Helper to format odds with +/- sign
  const formatOdds = (odds) => {
    if (odds === null || odds === undefined || odds === '') return '-';
    const numOdds = parseInt(odds, 10);
    if (isNaN(numOdds)) return '-';
    return numOdds > 0 ? `+${numOdds}` : `${numOdds}`;
  };

  // Calculate implied probability from American odds
  const calculateImpliedProbability = (odds) => {
    const numericOdds = parseInt(odds, 10);
    if (isNaN(numericOdds)) return null;
    if (numericOdds > 0) {
      return (100 / (numericOdds + 100)) * 100;
    } else {
      return (Math.abs(numericOdds) / (Math.abs(numericOdds) + 100)) * 100;
    }
  };

  // Handle odds cell hover/click to show tooltip
  const handleOddsHover = (event, odds, book, bestBook, bestBookDiff) => {
    if (!odds || odds === '-') return;

    const impliedProb = calculateImpliedProbability(odds);
    if (!impliedProb) return;

    // Only show edge for the best book (when there's a single best book, not "multi")
    // Also check aliases for book name matching
    const bookAlias = BOOKMAKER_ALIASES[book] || book;
    const reverseAlias = REVERSE_ALIASES[book] || book;
    const isBestBook = bestBook && bestBook !== 'multi' &&
      (book === bestBook || bookAlias === bestBook || reverseAlias === bestBook);
    const edge = (isBestBook && bestBookDiff) ? bestBookDiff * 100 : 0;

    let x, y;
    if (isMobile) {
      x = window.innerWidth / 2 - 100;
      y = Math.min(event.clientY, window.innerHeight - 150);
    } else {
      x = event.clientX;
      y = event.clientY;
    }

    setTooltipData({
      odds,
      book,
      impliedProb: impliedProb.toFixed(2),
      edge: edge.toFixed(2),
      x,
      y
    });
  };

  // Calculate American odds diff between two odds values
  const calculateAmericanOddsDiff = (bestOdds, secondOdds) => {
    if (bestOdds === null || bestOdds === undefined || secondOdds === null || secondOdds === undefined) {
      return 0;
    }

    // Both same sign
    if ((bestOdds > 0 && secondOdds > 0) || (bestOdds < 0 && secondOdds < 0)) {
      return Math.abs((bestOdds - secondOdds) / secondOdds);
    }
    // Best positive, second negative (crossing even money)
    else if (bestOdds > 0 && secondOdds < 0) {
      const bestDistance = bestOdds - 100;
      const secondDistance = secondOdds + 100;
      return Math.abs((bestDistance - secondDistance) / secondOdds);
    }
    // Best negative, second positive (crossing even money)
    else {
      const bestDistance = bestOdds + 100;
      const secondDistance = secondOdds - 100;
      return Math.abs((bestDistance - secondDistance) / secondOdds);
    }
  };

  // Get tier style based on diff value
  const getTierStyle = (diffValue) => {
    if (diffValue >= 0.25) {
      return { backgroundColor: 'green', color: 'yellow' };
    } else if (diffValue >= 0.15) {
      return { backgroundColor: '#F59E0B', color: '#1E40AF' };
    } else if (diffValue >= 0.05) {
      return { backgroundColor: '#3B82F6', color: 'white' };
    }
    return null;
  };

  // Get the cell style for best odds highlighting with edge tier colors
  const getCellStyle = (odds, allOddsForSide, bestBookDiff = null, bestBook = null, currentBook = null, allOddsWithBooks = null) => {
    if (!odds || !allOddsForSide || allOddsForSide.length === 0) return {};

    const validOdds = allOddsForSide.filter(o => o !== null && o !== undefined);
    if (validOdds.length <= 1) return {};

    const bestOdds = Math.max(...validOdds);
    const booksWithBest = validOdds.filter(o => o === bestOdds).length;

    // Check if bet365 is the best book
    const isBet365Best = bestBook === 'bet365';

    // Highlight if this is the best price
    if (odds === bestOdds) {
      const style = { fontWeight: 'bold', borderRadius: '3px' };

      // If only 1 book has the best price, use tier colors based on edge
      if (booksWithBest === 1 && bestBookDiff !== null && bestBookDiff !== undefined) {
        const diffValue = Math.abs(bestBookDiff);
        const tierStyle = getTierStyle(diffValue);
        if (tierStyle) {
          Object.assign(style, tierStyle);
        } else {
          // Small edge - just text highlight, no background
          Object.assign(style, { color: '#F59E0B' });
        }
      } else {
        // Multiple books have best price OR no diff data - just text highlight
        Object.assign(style, { color: '#F59E0B' });
      }

      return style;
    }

    // NCAAB special case: If bet365 is the best book, also apply tier coloring to next best book
    if (isBet365Best && allOddsWithBooks && currentBook) {
      // Get odds from books OTHER than bet365, sorted by odds descending
      const otherBooksOdds = [...allOddsWithBooks]
        .filter(o => o.odds !== null && o.odds !== undefined && o.book !== 'bet365')
        .sort((a, b) => b.odds - a.odds);

      if (otherBooksOdds.length >= 1) {
        const nextBestBook = otherBooksOdds[0].book;
        const nextBestOdds = otherBooksOdds[0].odds;

        // Check if current book is the next best book
        if (currentBook === nextBestBook) {
          // Calculate diff between 2nd and 3rd best (need at least 2 other books)
          if (otherBooksOdds.length >= 2) {
            const thirdBestOdds = otherBooksOdds[1].odds;
            const diff = calculateAmericanOddsDiff(nextBestOdds, thirdBestOdds);
            const tierStyle = getTierStyle(diff);
            if (tierStyle) {
              return { fontWeight: 'bold', borderRadius: '3px', ...tierStyle };
            }
          }
        }
      }
    }

    return {};
  };

  // Process market data into structured format
  const processMarketData = () => {
    if (!marketData?.markets?.[0]) return { h2h: [], spreads: [], totals: [] };

    const container = marketData.markets[0];
    return {
      h2h: container.h2h || [],
      spreads: container.spreads || [],
      totals: container.totals || []
    };
  };

  // Render a market table (h2h, spreads, or totals)
  const renderMarketTable = (records, marketType, title) => {
    if (!records || records.length === 0) return null;

    // For h2h and totals, use standard sides
    // For spreads, we need all 4 combinations
    const sides = marketType === 'totals' ? ['over', 'under'] : ['away', 'home'];
    const sideLabels = marketType === 'totals'
      ? { over: 'Over', under: 'Under' }
      : { away: game?.away || 'Away', home: game?.home || 'Home' };

    // For spreads, define all 4 combinations
    const spreadCombinations = [
      { side: 'away', sign: 'plus', label: game?.away || 'Away', displaySign: '+' },
      { side: 'away', sign: 'minus', label: game?.away || 'Away', displaySign: '-' },
      { side: 'home', sign: 'plus', label: game?.home || 'Home', displaySign: '+' },
      { side: 'home', sign: 'minus', label: game?.home || 'Home', displaySign: '-' }
    ];

    // Group records by point value
    const pointGroups = {};
    records.forEach(record => {
      const point = record.point ?? 'main';
      if (!pointGroups[point]) {
        pointGroups[point] = record;
      }
    });

    // Sort points numerically
    const sortedPoints = Object.keys(pointGroups).sort((a, b) => {
      if (a === 'main') return -1;
      if (b === 'main') return 1;
      return parseFloat(a) - parseFloat(b);
    });

    // Helper to render a header row
    const renderHeaderRow = (keyPrefix) => (
      <tr>
        <th style={{ width: '80px', minWidth: '80px' }}>
          {marketType === 'totals' ? 'Total' : 'Line'}
        </th>
        {marketType === 'spreads' && (
          <th style={{ width: '40px', minWidth: '40px' }}>+/-</th>
        )}
        <th style={{ width: '60px', minWidth: '60px' }}>Side</th>
        {bookmakers.map(book => (
          <th key={`${keyPrefix}-${book}`} style={{ width: '70px', minWidth: '70px' }}>
            <img
              src={getBookLogoUrl(book)}
              alt={book}
              style={{ height: '18px' }}
              title={book}
            />
          </th>
        ))}
      </tr>
    );

    return (
      <div className="mb-4">
        <h6 style={{ color: 'rgb(245, 158, 11)', fontWeight: 'bold', marginBottom: '10px' }}>
          {title}
        </h6>
        <div style={{ overflowX: 'auto' }}>
          <Table bordered size="sm" className="text-center" style={{ fontSize: '0.85rem' }}>
            <thead>
              {renderHeaderRow('thead')}
            </thead>
            <tbody>
              {sortedPoints.map(point => {
                const record = pointGroups[point];
                const pointDisplay = point === 'main' ? '-' : point;

                // For spreads, handle all 4 combinations
                if (marketType === 'spreads') {
                  // Helper to get odds for a specific combination
                  const getSpreadOddsForBook = (book, sign, side) => {
                    const key = `spreads_${sign}_${side}_${book}`;
                    let value = record[key];
                    let isAvailable = record[`${key}_isAvailable`] !== false;
                    if (value !== undefined) return { odds: value, isAvailable };
                    // Try alias
                    const aliasBook = REVERSE_ALIASES[book];
                    if (aliasBook) {
                      const aliasKey = `spreads_${sign}_${side}_${aliasBook}`;
                      value = record[aliasKey];
                      isAvailable = record[`${aliasKey}_isAvailable`] !== false;
                      if (value !== undefined) return { odds: value, isAvailable };
                    }
                    return undefined;
                  };

                  // Check which combinations have data
                  const rows = [];
                  let hasAnyEdge = false; // Track if any combination has a single-book edge

                  for (const combo of spreadCombinations) {
                    const booksWithOdds = bookmakers.filter(book =>
                      getSpreadOddsForBook(book, combo.sign, combo.side)?.odds !== undefined
                    );
                    if (booksWithOdds.length > 0) {
                      // Check if this combination has a single-book edge (excluding unavailable)
                      const oddsWithAvailability = booksWithOdds.map(book => {
                        const result = getSpreadOddsForBook(book, combo.sign, combo.side);
                        return { odds: result?.odds, isAvailable: result?.isAvailable !== false };
                      }).filter(o => o.odds !== undefined && o.odds !== null);

                      let comboHasEdge = false;
                      if (oddsWithAvailability.length > 1) {
                        const bestOdds = Math.max(...oddsWithAvailability.map(o => o.odds));
                        const booksWithBest = oddsWithAvailability.filter(o => o.odds === bestOdds);
                        // Only count as edge if exactly 1 book has best AND it's available
                        if (booksWithBest.length === 1 && booksWithBest[0].isAvailable) {
                          comboHasEdge = true;
                          hasAnyEdge = true;
                        }
                      }

                      // If showEdgesOnly, skip combos without edges
                      if (showEdgesOnly && !comboHasEdge) continue;

                      rows.push(combo);
                    }
                  }

                  if (rows.length === 0) return null;

                  // Skip this point value if showEdgesOnly is on and no single-book edge exists
                  if (showEdgesOnly && !hasAnyEdge) return null;

                  return rows.map((combo, comboIdx) => {
                    const allOddsForCombo = bookmakers.map(book =>
                      getSpreadOddsForBook(book, combo.sign, combo.side)?.odds
                    ).filter(o => o !== undefined && o !== null);

                    return (
                      <tr key={`${point}-${combo.sign}-${combo.side}`}>
                        {comboIdx === 0 && (
                          <td
                            rowSpan={rows.length}
                            style={{
                              verticalAlign: 'middle',
                              fontWeight: 'bold',
                              backgroundColor: 'rgba(0,0,0,0.02)'
                            }}
                          >
                            {pointDisplay}
                          </td>
                        )}
                        <td style={{
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          color: combo.displaySign === '+' ? '#10B981' : '#EF4444'
                        }}>
                          {combo.displaySign}
                        </td>
                        <td style={{ fontWeight: '500' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <img
                              src={combo.side === 'away' ? (game?.awayLogo || getTeamLogoUrl(game?.away)) : (game?.homeLogo || getTeamLogoUrl(game?.home))}
                              alt={combo.label}
                              style={{ width: '16px', height: '16px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <span style={{ fontSize: '0.75rem' }}>{combo.label}</span>
                          </div>
                        </td>
                        {(() => {
                          const bestBook = record[`spreads_bestBook_${combo.sign}_${combo.side}`];
                          const bestBookDiff = record[`spreads_bestBookDiff_${combo.sign}_${combo.side}`];
                          // Build array of odds with book names for next best book logic
                          const allOddsWithBooks = bookmakers.map(b => {
                            const result = getSpreadOddsForBook(b, combo.sign, combo.side);
                            return { book: b, odds: result?.odds };
                          }).filter(o => o.odds !== null && o.odds !== undefined);

                          return bookmakers.map(book => {
                            const result = getSpreadOddsForBook(book, combo.sign, combo.side);
                            const odds = result?.odds;
                            const isAvailable = result?.isAvailable !== false;
                            const cellStyle = getCellStyle(odds, allOddsForCombo, bestBookDiff, bestBook, book, allOddsWithBooks);
                            return (
                              <td
                                key={book}
                                style={{ padding: '4px', opacity: !isAvailable ? 0.25 : undefined, cursor: odds ? 'pointer' : 'default' }}
                                onClick={(e) => isMobile && odds && handleOddsHover(e, odds, book, bestBook, bestBookDiff)}
                                onMouseEnter={(e) => !isMobile && odds && handleOddsHover(e, odds, book, bestBook, bestBookDiff)}
                                onMouseLeave={() => !isMobile && setTooltipData(null)}
                              >
                                {odds !== undefined && odds !== null ? (
                                  <div style={{ ...cellStyle, padding: '2px 4px' }}>
                                    {formatOdds(odds)}
                                  </div>
                                ) : '-'}
                              </td>
                            );
                          });
                        })()}
                      </tr>
                    );
                  });
                }

                // For h2h and totals, use standard sides approach
                // Helper to get odds with alias fallback (needs side parameter)
                const getOddsForBook = (book, side) => {
                  // Pattern 1: marketType_book_side (e.g., h2h_fanduel_home)
                  let value = record[`${marketType}_${book}_${side}`];
                  if (value !== undefined) return value;
                  // Pattern 2: marketType_side_book (e.g., totals_over_fanduel)
                  value = record[`${marketType}_${side}_${book}`];
                  if (value !== undefined) return value;
                  // Try reverse alias with both patterns
                  const aliasBook = REVERSE_ALIASES[book];
                  if (aliasBook) {
                    value = record[`${marketType}_${aliasBook}_${side}`];
                    if (value !== undefined) return value;
                    value = record[`${marketType}_${side}_${aliasBook}`];
                    if (value !== undefined) return value;
                  }
                  return undefined;
                };

                // Helper to check availability for a book/side
                const getIsAvailable = (book, side) => {
                  // Check both patterns for availability flag
                  const key1 = `${marketType}_${book}_${side}_isAvailable`;
                  const key2 = `${marketType}_${side}_${book}_isAvailable`;
                  if (record[key1] === false || record[key2] === false) return false;
                  // Try alias
                  const aliasBook = REVERSE_ALIASES[book];
                  if (aliasBook) {
                    const aliasKey1 = `${marketType}_${aliasBook}_${side}_isAvailable`;
                    const aliasKey2 = `${marketType}_${side}_${aliasBook}_isAvailable`;
                    if (record[aliasKey1] === false || record[aliasKey2] === false) return false;
                  }
                  return true;
                };

                // Filter sides that have edges if showEdgesOnly is on (only for spreads/totals, not h2h)
                const filteredSides = (showEdgesOnly && marketType !== 'h2h') ? sides.filter(side => {
                  const oddsWithAvailability = bookmakers.map(book => ({
                    odds: getOddsForBook(book, side),
                    isAvailable: getIsAvailable(book, side)
                  })).filter(o => o.odds !== undefined && o.odds !== null);

                  if (oddsWithAvailability.length > 1) {
                    const bestOdds = Math.max(...oddsWithAvailability.map(o => o.odds));
                    const booksWithBest = oddsWithAvailability.filter(o => o.odds === bestOdds);
                    // Only include if exactly 1 book has best AND it's available
                    return booksWithBest.length === 1 && booksWithBest[0].isAvailable;
                  }
                  return false;
                }) : sides;

                // Skip this point if no sides pass the filter
                if (filteredSides.length === 0) return null;

                return filteredSides.map((side, sideIdx) => {
                  // Collect all odds for this side to determine best
                  const allOddsForSide = bookmakers.map(book => getOddsForBook(book, side))
                    .filter(o => o !== undefined && o !== null);

                  return (
                    <tr key={`${point}-${side}`}>
                      {sideIdx === 0 && (
                        <td
                          rowSpan={filteredSides.length}
                          style={{
                            verticalAlign: 'middle',
                            fontWeight: 'bold',
                            backgroundColor: 'rgba(0,0,0,0.02)'
                          }}
                        >
                          {pointDisplay}
                        </td>
                      )}
                      <td style={{ fontWeight: '500' }}>
                        {marketType === 'totals' ? (
                          <span style={{ color: side === 'over' ? '#10B981' : '#EF4444' }}>
                            {sideLabels[side]}
                          </span>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <img
                              src={side === 'away' ? (game?.awayLogo || getTeamLogoUrl(game?.away)) : (game?.homeLogo || getTeamLogoUrl(game?.home))}
                              alt={sideLabels[side]}
                              style={{ width: '16px', height: '16px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <span style={{ fontSize: '0.75rem' }}>{sideLabels[side]}</span>
                          </div>
                        )}
                      </td>
                      {(() => {
                        const bestBook = record[`${marketType}_bestBook_${side}`];
                        const bestBookDiff = record[`${marketType}_bestBookDiff_${side}`];
                        // Build array of odds with book names for next best book logic
                        const allOddsWithBooks = bookmakers.map(b => ({
                          book: b,
                          odds: getOddsForBook(b, side)
                        })).filter(o => o.odds !== null && o.odds !== undefined);

                        return bookmakers.map(book => {
                          const odds = getOddsForBook(book, side);
                          const isAvailable = getIsAvailable(book, side);
                          const cellStyle = getCellStyle(odds, allOddsForSide, bestBookDiff, bestBook, book, allOddsWithBooks);

                          return (
                            <td
                              key={book}
                              style={{ padding: '4px', opacity: !isAvailable ? 0.25 : undefined, cursor: odds ? 'pointer' : 'default' }}
                              onClick={(e) => isMobile && odds && handleOddsHover(e, odds, book, bestBook, bestBookDiff)}
                              onMouseEnter={(e) => !isMobile && odds && handleOddsHover(e, odds, book, bestBook, bestBookDiff)}
                              onMouseLeave={() => !isMobile && setTooltipData(null)}
                            >
                              {odds !== undefined && odds !== null ? (
                                <div style={{ ...cellStyle, padding: '2px 4px' }}>
                                  {formatOdds(odds)}
                                </div>
                              ) : (
                                <span style={{ color: '#ccc' }}>-</span>
                              )}
                            </td>
                          );
                        });
                      })()}
                    </tr>
                  );
                });
              })}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  const markets = processMarketData();

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="ncaab-game-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img
              src={game?.awayLogo || getTeamLogoUrl(game?.away)}
              alt={game?.away}
              style={{ width: '32px', height: '32px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span>{game?.away || 'Away'}</span>
          </div>
          <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>@</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img
              src={game?.homeLogo || getTeamLogoUrl(game?.home)}
              alt={game?.home}
              style={{ width: '32px', height: '32px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span>{game?.home || 'Home'}</span>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading odds...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-4 text-danger">
            <p>Error loading data: {error}</p>
          </div>
        )}

        {!loading && !error && marketData && (
          <>
            {/* Moneyline - Always visible */}
            {renderMarketTable(markets.h2h, 'h2h', 'Moneyline')}

            {/* Toggles for Spreads and Totals - mutually exclusive */}
            <div className="d-flex justify-content-center align-items-center gap-2 mb-3">
              <Button
                variant={showSpreads ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => { setShowSpreads(true); setShowTotals(false); }}
              >
                Spreads
              </Button>
              <Button
                variant={showTotals ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => { setShowTotals(true); setShowSpreads(false); }}
              >
                Totals
              </Button>
              <span style={{ borderLeft: '1px solid #6c757d', height: '24px', margin: '0 8px' }} />
              <Button
                variant={showEdgesOnly ? 'warning' : 'outline-secondary'}
                size="sm"
                onClick={() => setShowEdgesOnly(!showEdgesOnly)}
                title="Show all lines where there is 1 book with an edge"
              >
                Show Edges
              </Button>
            </div>

            {/* Spreads - Togglable */}
            {showSpreads && renderMarketTable(markets.spreads, 'spreads', 'Spreads')}

            {/* Totals - Togglable */}
            {showTotals && renderMarketTable(markets.totals, 'totals', 'Totals')}
          </>
        )}

        {!loading && !error && !marketData && (
          <div className="text-center py-4 text-muted">
            <p>No odds data available for this game.</p>
          </div>
        )}
      </Modal.Body>

      {/* Edge % Tooltip */}
      {tooltipData && (
        <div
          style={{
            position: 'fixed',
            left: `${tooltipData.x + 10}px`,
            top: `${tooltipData.y + 10}px`,
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 9999,
            fontSize: '0.85rem',
            minWidth: '200px',
            pointerEvents: 'none'
          }}
        >
          <div style={{ marginBottom: '8px', fontWeight: 'bold', borderBottom: '1px solid #4b5563', paddingBottom: '6px' }}>
            {tooltipData.book.charAt(0).toUpperCase() + tooltipData.book.slice(1).replace(/_/g, ' ')}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Odds:</span>
            <span style={{ fontWeight: 'bold' }}>{tooltipData.odds > 0 ? `+${tooltipData.odds}` : tooltipData.odds}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Implied Prob:</span>
            <span style={{ fontWeight: 'bold', color: '#60a5fa' }}>{tooltipData.impliedProb}%</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Edge:</span>
            <span style={{ fontWeight: 'bold', color: parseFloat(tooltipData.edge) > 0 ? '#34d399' : '#9ca3af' }}>
              {parseFloat(tooltipData.edge) > 0 ? '+' : ''}{tooltipData.edge}%
            </span>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default NCAABGameModal;
